// @flow

import * as React from "react";
import SVG from "react-inlinesvg";
import cx from "classnames";
import ClampLines from "react-clamp-lines";
import Preloader from "../Preloader";

import styles from "./FileWidget.scss";

export type IndicatorStyle = "red" | "yellow" | "green" | "green-dashed" | "grey";

export type DropZoneEvent = SyntheticDragEvent<HTMLDivElement>;

type FileWidgetProps = {|
  cta: ?() => React.Node,
  indicatorStyle: IndicatorStyle,
  shouldShowPreloader: boolean,
  title: string,
  moveIndicator: boolean,
  icon: string,
  onDrop: ?(DropZoneEvent) => void,
  uploadDate: string,
  filename: ?string
|};

const onDragOver = (event: DropZoneEvent) => {
  event.preventDefault();
};

const createOnDrop = (onDrop: (event: DropZoneEvent) => void) => (event: DropZoneEvent) => {
  event.preventDefault();
  onDrop(event);
};

function formatDate(dateString) {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${day}.${month}.${year} - ${hours}:${minutes} Uhr`;
}

const FileWidget = ({
  cta,
  indicatorStyle,
  shouldShowPreloader,
  title,
  moveIndicator,
  icon,
  onDrop,
  uploadDate,
  filename
}: FileWidgetProps) => {
  return (
    <section className={styles.container}>
      <div className={styles.innerWrapper}>
        <header title={title} className={styles.title}>
          <span className={styles.titleText}>{title}</span>
          {filename && (
            <ClampLines
              className={styles.uploadedDate}
              text={filename}
              lines={2}
              ellipsis="..."
              buttons={false}
            />
          )}
          {uploadDate && (
            <ClampLines
              className={styles.uploadedDate}
              text={`Hochgeladen am ${formatDate(uploadDate)}`}
              lines={2}
              ellipsis="..."
              buttons={false}
            />
          )}
        </header>
        <div className={cx(styles.inner)}>
          <div
            className={cx(styles.indicator, {
              [styles.red]: indicatorStyle === "red",
              [styles.yellow]: indicatorStyle === "yellow",
              [styles.green]: indicatorStyle === "green",
              [styles.greenDashed]: indicatorStyle === "green-dashed",
              [styles.grey]: indicatorStyle === "grey",
              [styles.moveIndicator]: moveIndicator
            })}
          >
            {shouldShowPreloader ? (
              <Preloader />
            ) : (
              <SVG src={icon} cacheGetRequests className={styles.icon} />
            )}
          </div>
          {cta && <div>{cta()}</div>}
        </div>
        {onDrop && (
          <div className={styles.dropZone} onDrop={createOnDrop(onDrop)} onDragOver={onDragOver} />
        )}
      </div>
    </section>
  );
};

FileWidget.defaultProps = {
  cta: null,
  shouldShowPreloader: false,
  moveIndicator: false
};

export default FileWidget;
